import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Search, RefreshCw, Save, Edit2, Trash2, Database, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const CONTRACTOR_TYPES = ['Concrete', 'Iron', 'Gutter', 'Painter', 'Other'];

/**
 * Admin matrix of component → contractor-type → labour rate.
 *
 * Layout: rows are unique (component_type · component_name) pairs.
 * Columns are the 5 contractor types. Cells are inline-editable.
 * "Seed from products" pulls initial rates from product_catalog.
 */
export default function ComponentLabourRatesPage() {
  const { api } = useAuth();
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // { component_type, component_name, contractor_type, _id, labour_rate }
  const [editValue, setEditValue] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newRow, setNewRow] = useState({ component_type: '', component_name: '', contractor_type: 'Concrete', labour_rate: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/component-labour-rates');
      setRates(Array.isArray(data) ? data : []);
    } catch { setRates([]); }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // Group rates into matrix rows.
  const rows = useMemo(() => {
    const term = search.toLowerCase().trim();
    const map = new Map();
    for (const r of rates) {
      const key = `${r.component_type}:::${r.component_name}`;
      if (term && !key.toLowerCase().includes(term)) continue;
      if (!map.has(key)) {
        map.set(key, {
          component_type: r.component_type,
          component_name: r.component_name,
          cells: {},
        });
      }
      map.get(key).cells[r.contractor_type] = r;
    }
    return [...map.values()].sort((a, b) => {
      const at = (a.component_type || '').localeCompare(b.component_type || '');
      if (at !== 0) return at;
      return (a.component_name || '').localeCompare(b.component_name || '');
    });
  }, [rates, search]);

  const seedFromProducts = async () => {
    if (!window.confirm('Seed from product_catalog? This will create/update rate rows for every active product.')) return;
    try {
      const { data } = await api.post('/component-labour-rates/seed-from-products');
      toast.success(`Seeded ${data.seeded} rates · skipped ${data.skipped}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Seed failed'); }
  };

  const beginEdit = (row, ctype) => {
    const existing = row.cells[ctype];
    setEditing({
      component_type: row.component_type,
      component_name: row.component_name,
      contractor_type: ctype,
      _id: existing?._id,
    });
    setEditValue(existing ? String(existing.labour_rate ?? 0) : '0');
  };

  const saveCell = async () => {
    if (!editing) return;
    const payload = {
      component_type: editing.component_type,
      component_name: editing.component_name,
      contractor_type: editing.contractor_type,
      labour_rate: parseFloat(editValue) || 0,
      notes: '',
      active: true,
    };
    try {
      if (editing._id) {
        await api.patch(`/component-labour-rates/${editing._id}`, payload);
      } else {
        await api.post('/component-labour-rates', payload);
      }
      toast.success(`Updated ${editing.component_name} · ${editing.contractor_type}`);
      setEditing(null); setEditValue('');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const deleteCell = async (rateId, label) => {
    if (!window.confirm(`Delete rate for ${label}?`)) return;
    try {
      await api.delete(`/component-labour-rates/${rateId}`);
      toast.success('Rate deleted');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const addRow = async () => {
    if (!newRow.component_type || !newRow.component_name) {
      toast.error('Component type and name are required');
      return;
    }
    try {
      await api.post('/component-labour-rates', {
        ...newRow,
        labour_rate: parseFloat(newRow.labour_rate) || 0,
        notes: '', active: true,
      });
      toast.success(`Added ${newRow.component_name}`);
      setShowAdd(false);
      setNewRow({ component_type: '', component_name: '', contractor_type: 'Concrete', labour_rate: 0 });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
  };

  return (
    <div data-testid="component-labour-rates-page" className="space-y-6 p-6">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Database size={20} className="text-[#D35400]" />
            Component Labour Rates
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Per-component labour rate per contractor type. Used by the Contractor Sheet PDF.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="seed-from-products-btn"
            onClick={seedFromProducts}
            className="flex items-center gap-2 px-3 py-2 text-sm font-semibold border border-[#D35400] text-[#D35400] rounded-md hover:bg-[#D35400] hover:text-white transition-colors"
          >
            <RefreshCw size={14} /> Seed from products
          </button>
          <button
            data-testid="add-rate-btn"
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-semibold bg-[#D35400] text-white rounded-md hover:bg-[#B84500]"
          >
            <Plus size={14} /> Add component
          </button>
        </div>
      </header>

      <div className="bg-white border border-[#E2E2DF] rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Search size={16} className="text-slate-400" />
          <input
            data-testid="rates-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Filter by component type or name…"
            className="flex-1 px-3 py-1.5 border border-slate-200 rounded text-sm"
          />
          <span className="text-xs text-slate-500">{rows.length} components · {rates.length} rates</span>
        </div>

        {loading ? (
          <p className="text-center text-sm text-slate-400 py-12">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="text-center py-12">
            <AlertCircle size={28} className="mx-auto text-amber-500 mb-2" />
            <p className="text-sm font-semibold text-slate-700">No rates yet.</p>
            <p className="text-xs text-slate-500 mt-1">Click <strong>Seed from products</strong> to import labour rates from the product catalog.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E2E2DF]">
                  <th className="text-left text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 py-2 px-2">Component Type</th>
                  <th className="text-left text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 py-2 px-2">Component Name</th>
                  {CONTRACTOR_TYPES.map(ct => (
                    <th key={ct} className="text-right text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 py-2 px-2">{ct}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={`${row.component_type}-${row.component_name}-${i}`} className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="py-1.5 px-2 text-slate-700 text-xs uppercase tracking-wide">{row.component_type}</td>
                    <td className="py-1.5 px-2 font-medium text-slate-800">{row.component_name}</td>
                    {CONTRACTOR_TYPES.map(ct => {
                      const cell = row.cells[ct];
                      const isEditing = editing
                        && editing.component_type === row.component_type
                        && editing.component_name === row.component_name
                        && editing.contractor_type === ct;
                      return (
                        <td key={ct} className="py-1.5 px-2 text-right" data-testid={`cell-${row.component_type}-${row.component_name}-${ct}`}>
                          {isEditing ? (
                            <div className="flex items-center justify-end gap-1">
                              <input
                                data-testid="cell-input"
                                autoFocus
                                type="number" step="0.01"
                                value={editValue}
                                onChange={e => setEditValue(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') saveCell();
                                  if (e.key === 'Escape') { setEditing(null); setEditValue(''); }
                                }}
                                className="w-20 px-1.5 py-0.5 border border-[#D35400] rounded text-right text-xs font-mono"
                              />
                              <button data-testid="cell-save" onClick={saveCell} className="text-emerald-600 hover:text-emerald-700"><Save size={11} /></button>
                              <button onClick={() => { setEditing(null); setEditValue(''); }} className="text-slate-400 hover:text-slate-600"><X size={11} /></button>
                            </div>
                          ) : cell ? (
                            <div className="flex items-center justify-end gap-1.5 group">
                              <span className="font-mono text-slate-800">${(cell.labour_rate || 0).toFixed(2)}</span>
                              <button
                                data-testid="cell-edit"
                                onClick={() => beginEdit(row, ct)}
                                className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-[#D35400] transition-opacity"
                              ><Edit2 size={10} /></button>
                              <button
                                data-testid="cell-delete"
                                onClick={() => deleteCell(cell._id, `${row.component_name} · ${ct}`)}
                                className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-opacity"
                              ><Trash2 size={10} /></button>
                            </div>
                          ) : (
                            <button
                              data-testid="cell-add"
                              onClick={() => beginEdit(row, ct)}
                              className="text-slate-300 hover:text-[#D35400] text-xs"
                            >+</button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5 space-y-4">
            <header className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-800">Add Component Rate</h2>
              <button onClick={() => setShowAdd(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </header>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Component Type</label>
                <input data-testid="new-rate-type" value={newRow.component_type} onChange={e => setNewRow({ ...newRow, component_type: e.target.value })} placeholder="e.g. Tile, Ridge, Valley" className="w-full px-3 py-2 border border-slate-200 rounded text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Component Name</label>
                <input data-testid="new-rate-name" value={newRow.component_name} onChange={e => setNewRow({ ...newRow, component_name: e.target.value })} placeholder="e.g. Replace Old Tiles" className="w-full px-3 py-2 border border-slate-200 rounded text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Contractor Type</label>
                  <select data-testid="new-rate-ctype" value={newRow.contractor_type} onChange={e => setNewRow({ ...newRow, contractor_type: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded text-sm bg-white">
                    {CONTRACTOR_TYPES.map(ct => <option key={ct} value={ct}>{ct}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Labour Rate ($)</label>
                  <input data-testid="new-rate-amount" type="number" step="0.01" value={newRow.labour_rate} onChange={e => setNewRow({ ...newRow, labour_rate: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded text-sm" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 border border-slate-200 rounded text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
              <button data-testid="new-rate-save" onClick={addRow} className="px-3 py-1.5 bg-[#D35400] text-white rounded text-sm font-semibold hover:bg-[#B84500]">Add</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
